/**
 * Guest Profile Management Service - Phase 4
 *
 * This service manages personalized guest profiles with preference tracking,
 * history management, and cross-visit personalization for enhanced user experience.
 *
 * Key Features:
 * - Persistent guest preference tracking
 * - Visit history and tasting notes
 * - Personalized recommendation learning
 * - Favorites and wishlist management
 * - Social sharing capabilities
 * - Privacy-compliant data management
 */

import { advancedRecommendationEngine } from "./advancedRecommendationEngine.js";

class GuestProfileManager {
  constructor() {
    this.guestProfiles = new Map(); // In-memory storage (would be database in production)
    this.sessionData = new Map();
    this.privacySettings = {
      dataRetentionDays: 365,
      anonymousMode: false,
      shareWithRestaurant: true,
    };
  }

  /**
   * Create or retrieve guest profile
   * @param {string} guestId - Unique guest identifier
   * @param {Object} initialData - Initial profile data
   * @returns {Object} Guest profile object
   */
  async getOrCreateGuestProfile(guestId, initialData = {}) {
    try {
      if (this.guestProfiles.has(guestId)) {
        const profile = this.guestProfiles.get(guestId);
        profile.lastVisit = new Date();
        profile.visitCount = (profile.visitCount || 0) + 1;
        return profile;
      }

      // Create new guest profile
      const newProfile = {
        guestId,
        createdAt: new Date(),
        lastVisit: new Date(),
        visitCount: 1,
        preferences: {
          beverageTypes: initialData.beverageTypes || [],
          flavorPreferences: initialData.flavorPreferences || [],
          priceRanges: initialData.priceRanges || {
            preferred: { min: 0, max: 100 },
          },
          regions: initialData.regions || [],
          occasions: initialData.occasions || [],
          explorationFactor: initialData.explorationFactor || 0.3,
          dietaryRestrictions: initialData.dietaryRestrictions || [],
        },
        history: {
          tastings: [],
          orders: [],
          recommendations: [],
          searches: [],
          favorites: [],
          wishlist: [],
        },
        socialProfile: {
          sharePreferences: false,
          publicReviews: false,
          socialPlatforms: [],
        },
        privacySettings: { ...this.privacySettings },
        learningMetrics: {
          accuracyScore: 0.5,
          engagementLevel: "new",
          preferenceConfidence: 0.1,
        },
      };

      this.guestProfiles.set(guestId, newProfile);
      return newProfile;
    } catch (error) {
      console.error("Guest profile creation error:", error);
      return this.createFallbackProfile(guestId);
    }
  }

  /**
   * Update guest preferences based on interaction
   * @param {string} guestId - Guest identifier
   * @param {Object} interaction - Interaction data
   * @returns {Object} Updated preferences
   */
  async updateGuestPreferences(guestId, interaction) {
    try {
      const profile = await this.getOrCreateGuestProfile(guestId);

      // Record the interaction
      this.recordInteraction(profile, interaction);

      // Update preferences based on interaction type
      switch (interaction.type) {
        case "like":
          await this.processLikeInteraction(profile, interaction);
          break;
        case "dislike":
          await this.processDislikeInteraction(profile, interaction);
          break;
        case "order":
          await this.processOrderInteraction(profile, interaction);
          break;
        case "search":
          await this.processSearchInteraction(profile, interaction);
          break;
        case "comparison":
          await this.processComparisonInteraction(profile, interaction);
          break;
        default:
          console.log("Unknown interaction type:", interaction.type);
          break;
      }

      // Update learning metrics
      this.updateLearningMetrics(profile, interaction);

      // Save updated profile
      this.guestProfiles.set(guestId, profile);

      return profile.preferences;
    } catch (error) {
      console.error("Preference update error:", error);
      return null;
    }
  }

  /**
   * Get personalized recommendations for guest
   * @param {string} guestId - Guest identifier
   * @param {Array} inventory - Available inventory
   * @param {Object} context - Current context (occasion, mood, etc.)
   * @returns {Object} Personalized recommendations
   */
  async getPersonalizedRecommendations(guestId, inventory, context = {}) {
    try {
      const profile = await this.getOrCreateGuestProfile(guestId);

      // Enhance context with guest history
      const enhancedContext = {
        ...context,
        guestHistory: this.summarizeGuestHistory(profile),
        visitNumber: profile.visitCount,
        learningConfidence: profile.learningMetrics.preferenceConfidence,
      };

      // Get base recommendations from Phase 2 engine
      const baseRecommendations =
        await advancedRecommendationEngine.getPersonalizedRecommendations(
          // NOSONAR
          profile.preferences,
          inventory,
          enhancedContext
        );

      // Enhance with guest-specific insights
      const enhancedRecommendations = await this.enhanceWithGuestInsights(
        baseRecommendations,
        profile,
        context
      );

      // Filter out items guest has shown dislike for
      const filteredRecommendations = this.filterBasedOnHistory(
        enhancedRecommendations,
        profile
      );

      // Record recommendation event
      this.recordRecommendationEvent(profile, filteredRecommendations, context);

      return {
        ...filteredRecommendations,
        guestContext: {
          visitNumber: profile.visitCount,
          confidenceLevel: profile.learningMetrics.preferenceConfidence,
          explorationRecommended: this.shouldRecommendExploration(profile),
          personalizedFactors: this.getPersonalizationFactors(profile),
        },
      };
    } catch (error) {
      console.error("Personalized recommendations error:", error);
      return {
        recommendations: [],
        confidence: 0.1,
        error: "Personalization temporarily unavailable",
      };
    }
  }

  /**
   * Manage guest favorites and wishlist
   * @param {string} guestId - Guest identifier
   * @param {string} action - 'add_favorite', 'remove_favorite', 'add_wishlist', 'remove_wishlist'
   * @param {Object} item - Beverage item
   * @returns {Object} Updated lists
   */
  async manageFavoritesAndWishlist(guestId, action, item) {
    try {
      const profile = await this.getOrCreateGuestProfile(guestId);

      switch (action) {
        case "add_favorite":
          if (!profile.history.favorites.find((fav) => fav.id === item.id)) {
            profile.history.favorites.push({
              ...item,
              addedDate: new Date(),
              timesOrdered: 0,
            });
          }
          break;

        case "remove_favorite":
          profile.history.favorites = profile.history.favorites.filter(
            (fav) => fav.id !== item.id
          );
          break;

        case "add_wishlist":
          if (!profile.history.wishlist.find((wish) => wish.id === item.id)) {
            profile.history.wishlist.push({
              ...item,
              addedDate: new Date(),
              priority: "medium",
              notes: "",
            });
          }
          break;

        case "remove_wishlist":
          profile.history.wishlist = profile.history.wishlist.filter(
            (wish) => wish.id !== item.id
          );
          break;

        default:
          console.log("Unknown favorites/wishlist action:", action);
          break;
      }

      // Update preferences based on favorites
      if (action.includes("favorite")) {
        await this.updatePreferencesFromFavorites(profile);
      }

      this.guestProfiles.set(guestId, profile);

      return {
        favorites: profile.history.favorites,
        wishlist: profile.history.wishlist,
        action: action,
        success: true,
      };
    } catch (error) {
      console.error("Favorites/wishlist management error:", error);
      return {
        favorites: [],
        wishlist: [],
        action: action,
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Generate guest insights for restaurant
   * @param {string} guestId - Guest identifier
   * @returns {Object} Guest insights for restaurant staff
   */
  async generateGuestInsights(guestId) {
    try {
      const profile = await this.getOrCreateGuestProfile(guestId);

      if (!profile.privacySettings.shareWithRestaurant) {
        return {
          insights: "Guest has opted out of sharing detailed insights",
          basicInfo: {
            visitCount: profile.visitCount,
            preferenceLevel: profile.learningMetrics.engagementLevel,
          },
        };
      }

      const insights = {
        guestProfile: {
          visitCount: profile.visitCount,
          memberSince: profile.createdAt,
          engagementLevel: profile.learningMetrics.engagementLevel,
          preferenceConfidence: profile.learningMetrics.preferenceConfidence,
        },
        preferences: {
          primaryBeverageTypes: this.getTopPreferences(
            profile.preferences.beverageTypes
          ),
          favoriteRegions: this.getTopPreferences(profile.preferences.regions),
          priceRange: profile.preferences.priceRanges.preferred,
          explorationTendency:
            profile.preferences.explorationFactor > 0.5
              ? "adventurous"
              : "conservative",
        },
        orderHistory: {
          favoriteOrders: this.getTopOrders(profile.history.orders),
          averageSpend: this.calculateAverageSpend(profile.history.orders),
          preferredOccasions: this.getTopPreferences(
            profile.preferences.occasions
          ),
        },
        recommendations: {
          suggestedFocus: this.getSuggestedFocus(profile),
          upsellOpportunities: this.getUpsellOpportunities(profile),
          specialConsiderations: this.getSpecialConsiderations(profile),
        },
      };

      return insights;
    } catch (error) {
      console.error("Guest insights generation error:", error);
      return {
        insights: "Error generating guest insights",
        error: error.message,
      };
    }
  }

  /**
   * Social sharing capabilities
   * @param {string} guestId - Guest identifier
   * @param {Object} shareData - Data to share
   * @param {string} platform - Social platform
   * @returns {Object} Share result
   */
  async shareBeverageDiscovery(guestId, shareData, platform) {
    try {
      const profile = await this.getOrCreateGuestProfile(guestId);

      if (!profile.socialProfile.sharePreferences) {
        return {
          success: false,
          message: "Social sharing not enabled for this guest",
        };
      }

      // Generate shareable content
      const shareableContent = this.generateShareableContent(
        shareData,
        profile
      );

      // Record social sharing event
      this.recordSocialShare(profile, shareData, platform);

      // In production, this would integrate with actual social platforms
      return {
        success: true,
        shareUrl: `https://sommelai.com/share/${shareData.beverageId}`,
        content: shareableContent,
        platform: platform,
        analytics: {
          shareCount: 1,
          expectedReach: this.estimateReach(platform, profile),
        },
      };
    } catch (error) {
      console.error("Social sharing error:", error);
      return {
        success: false,
        message: "Social sharing temporarily unavailable",
        error: error.message,
      };
    }
  }

  /**
   * Privacy and data management
   * @param {string} guestId - Guest identifier
   * @param {Object} privacySettings - Updated privacy settings
   * @returns {Object} Updated settings
   */
  async updatePrivacySettings(guestId, privacySettings) {
    try {
      const profile = await this.getOrCreateGuestProfile(guestId);

      profile.privacySettings = {
        ...profile.privacySettings,
        ...privacySettings,
      };

      // If guest opts out of data sharing, anonymize sensitive data
      if (!privacySettings.shareWithRestaurant) {
        this.anonymizeSensitiveData(profile);
      }

      this.guestProfiles.set(guestId, profile);

      return {
        success: true,
        settings: profile.privacySettings,
        message: "Privacy settings updated successfully",
      };
    } catch (error) {
      console.error("Privacy settings update error:", error);
      return {
        success: false,
        message: "Privacy settings update failed",
        error: error.message,
      };
    }
  }

  // Private helper methods

  recordInteraction(profile, interaction) {
    const interactionRecord = {
      ...interaction,
      timestamp: new Date(),
      sessionId: this.getCurrentSessionId(),
    };

    switch (interaction.type) {
      case "like":
      case "dislike":
        profile.history.tastings.push(interactionRecord);
        break;
      case "order":
        profile.history.orders.push(interactionRecord);
        break;
      case "search":
        profile.history.searches.push(interactionRecord);
        break;
      default:
        // For other interaction types, store in a general history
        if (!profile.history.general) {
          profile.history.general = [];
        }
        profile.history.general.push(interactionRecord);
        break;
    }
  }

  async processLikeInteraction(profile, interaction) {
    const beverage = interaction.beverage;

    // Update beverage type preferences
    if (
      beverage.type &&
      !profile.preferences.beverageTypes.includes(beverage.type)
    ) {
      profile.preferences.beverageTypes.push(beverage.type);
    }

    // Update region preferences
    if (
      beverage.region &&
      !profile.preferences.regions.includes(beverage.region)
    ) {
      profile.preferences.regions.push(beverage.region);
    }

    // Update flavor preferences from enhanced data
    if (beverage.enhancedData?.tastingNotes) {
      const flavors = [
        ...(beverage.enhancedData.tastingNotes.aroma || []),
        ...(beverage.enhancedData.tastingNotes.palate || []),
      ];

      flavors.forEach((flavor) => {
        if (!profile.preferences.flavorPreferences.includes(flavor)) {
          profile.preferences.flavorPreferences.push(flavor);
        }
      });
    }

    // Update price range if this represents an upward trend
    const price = this.extractPrice(beverage.price);
    if (price && price > profile.preferences.priceRanges.preferred.max) {
      profile.preferences.priceRanges.preferred.max = Math.min(
        price * 1.2, // Allow 20% stretch
        profile.preferences.priceRanges.preferred.max * 1.5
      );
    }
  }

  async processDislikeInteraction(profile, interaction) {
    const beverage = interaction.beverage;

    // Add to dislike tracking (implicit)
    if (!profile.history.dislikes) {
      profile.history.dislikes = [];
    }

    profile.history.dislikes.push({
      beverage: beverage,
      reason: interaction.reason || "unspecified",
      timestamp: new Date(),
    });

    // Adjust preferences to avoid similar items
    this.adjustPreferencesFromDislike(profile, beverage);
  }

  async processOrderInteraction(profile, interaction) {
    // Orders are strong preference indicators
    await this.processLikeInteraction(profile, {
      ...interaction,
      type: "like",
      strength: 1.0,
    });

    // Track spending patterns
    const price = this.extractPrice(interaction.beverage.price);
    if (price) {
      const orders = profile.history.orders;
      const averageSpend =
        orders.length > 0
          ? orders.reduce(
              (sum, order) => sum + this.extractPrice(order.beverage.price),
              0
            ) / orders.length
          : price;

      // Adjust preferred price range based on actual spending
      profile.preferences.priceRanges.preferred = {
        min: Math.min(
          profile.preferences.priceRanges.preferred.min,
          price * 0.7
        ),
        max: Math.max(
          profile.preferences.priceRanges.preferred.max,
          averageSpend * 1.3
        ),
      };
    }
  }

  async processSearchInteraction(profile, interaction) {
    // Search queries reveal intent and preferences
    const query = interaction.query.toLowerCase();

    // Extract intent from search terms
    const flavorKeywords = [
      "bold",
      "crisp",
      "smooth",
      "light",
      "rich",
      "dry",
      "sweet",
    ];
    const regionKeywords = [
      "napa",
      "tuscany",
      "bordeaux",
      "champagne",
      "california",
    ];

    flavorKeywords.forEach((keyword) => {
      if (
        query.includes(keyword) &&
        !profile.preferences.flavorPreferences.includes(keyword)
      ) {
        profile.preferences.flavorPreferences.push(keyword);
      }
    });

    regionKeywords.forEach((keyword) => {
      if (
        query.includes(keyword) &&
        !profile.preferences.regions.includes(keyword)
      ) {
        profile.preferences.regions.push(keyword);
      }
    });
  }

  updateLearningMetrics(profile, interaction) {
    // Update confidence based on interaction frequency
    profile.learningMetrics.preferenceConfidence = Math.min(
      1.0,
      profile.learningMetrics.preferenceConfidence + 0.05
    );

    // Update engagement level
    const totalInteractions =
      profile.history.tastings.length +
      profile.history.orders.length +
      profile.history.searches.length;

    if (totalInteractions > 20) {
      profile.learningMetrics.engagementLevel = "highly_engaged";
    } else if (totalInteractions > 5) {
      profile.learningMetrics.engagementLevel = "engaged";
    } else {
      profile.learningMetrics.engagementLevel = "new";
    }
  }

  summarizeGuestHistory(profile) {
    return {
      totalTastings: profile.history.tastings.length,
      totalOrders: profile.history.orders.length,
      favoriteCount: profile.history.favorites.length,
      mostOrderedTypes: this.getMostFrequent(
        profile.history.orders.map((order) => order.beverage.type)
      ),
      averageSpend: this.calculateAverageSpend(profile.history.orders),
      visitFrequency: this.calculateVisitFrequency(profile),
    };
  }

  enhanceWithGuestInsights(recommendations, profile, context) {
    return {
      ...recommendations,
      recommendations: recommendations.recommendations.map((rec) => ({
        ...rec,
        guestSpecificReasons: this.generateGuestSpecificReasons(rec, profile),
        personalizedConfidence: this.calculatePersonalizedConfidence(
          rec,
          profile
        ),
        newDiscovery: this.isNewDiscovery(rec.beverage, profile),
      })),
    };
  }

  filterBasedOnHistory(recommendations, profile) {
    const dislikedItems = profile.history.dislikes || [];
    const dislikedIds = new Set(dislikedItems.map((item) => item.beverage.id));

    return {
      ...recommendations,
      recommendations: recommendations.recommendations.filter(
        (rec) => !dislikedIds.has(rec.beverage.id)
      ),
    };
  }

  // Utility methods
  extractPrice(priceString) {
    if (!priceString) return null;
    const match = priceString.match(/\$?(\d+(?:\.\d{2})?)/);
    return match ? parseFloat(match[1]) : null;
  }

  getCurrentSessionId() {
    // In production, this would be a proper session management system
    return "session_" + Date.now();
  }

  createFallbackProfile(guestId) {
    return {
      guestId,
      preferences: {
        beverageTypes: [],
        flavorPreferences: [],
        priceRanges: { preferred: { min: 0, max: 100 } },
        regions: [],
        occasions: [],
        explorationFactor: 0.3,
      },
      history: {
        tastings: [],
        orders: [],
        recommendations: [],
        searches: [],
        favorites: [],
        wishlist: [],
      },
      learningMetrics: {
        accuracyScore: 0.5,
        engagementLevel: "new",
        preferenceConfidence: 0.1,
      },
      fallback: true,
    };
  }

  getMostFrequent(array) {
    const frequency = {};
    array.forEach((item) => (frequency[item] = (frequency[item] || 0) + 1));
    return Object.keys(frequency).sort(
      (a, b) => frequency[b] - frequency[a]
    )[0];
  }

  calculateAverageSpend(orders) {
    if (orders.length === 0) return 0;
    const total = orders.reduce((sum, order) => {
      const price = this.extractPrice(order.beverage.price);
      return sum + (price || 0);
    }, 0);
    return total / orders.length;
  }

  calculateVisitFrequency(profile) {
    const daysSinceFirst =
      (new Date() - new Date(profile.createdAt)) / (1000 * 60 * 60 * 24);
    return profile.visitCount / Math.max(daysSinceFirst, 1);
  }

  /**
   * Get guest profile summary for UI display
   * @param {string} guestId - Guest identifier
   * @returns {Object} Profile summary
   */
  async getGuestProfileSummary(guestId) {
    try {
      const profile = await this.getOrCreateGuestProfile(guestId);

      return {
        visitCount: profile.visitCount,
        memberSince: profile.createdAt,
        engagementLevel: profile.learningMetrics.engagementLevel,
        favoriteCount: profile.history.favorites.length,
        wishlistCount: profile.history.wishlist.length,
        preferenceConfidence: Math.round(
          profile.learningMetrics.preferenceConfidence * 100
        ),
        topPreferences: {
          beverageTypes: profile.preferences.beverageTypes.slice(0, 3),
          regions: profile.preferences.regions.slice(0, 3),
          flavors: profile.preferences.flavorPreferences.slice(0, 5),
        },
      };
    } catch (error) {
      console.error("Profile summary error:", error);
      return null;
    }
  }

  /**
   * Clear guest data (GDPR compliance)
   * @param {string} guestId - Guest identifier
   * @returns {Object} Deletion result
   */
  async deleteGuestData(guestId) {
    try {
      if (this.guestProfiles.has(guestId)) {
        this.guestProfiles.delete(guestId);
        return {
          success: true,
          message: "Guest data successfully deleted",
        };
      }

      return {
        success: false,
        message: "Guest profile not found",
      };
    } catch (error) {
      console.error("Data deletion error:", error);
      return {
        success: false,
        message: "Data deletion failed",
        error: error.message,
      };
    }
  }
}

// Create and export singleton instance
export const guestProfileManager = new GuestProfileManager();
export default guestProfileManager;
